/**
 * WebView Provider
 * Provides WebView context and authentication to the app
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useWebViewContext } from '../hooks/useWebViewContext';
import { useWebViewAuth } from '../hooks/useWebViewAuth';
import { useWebViewMessages } from '../hooks/useWebViewMessages';
import { WebViewExternalProps } from '../types';

interface WebViewContextValue {
  // Context
  isWebView: boolean;
  externalProps: WebViewExternalProps | null;
  isLoading: boolean;
  contextError: Error | null;
  
  // Auth
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  authError: Error | null;
  authenticate: () => Promise<void>;
  logout: () => Promise<void>;
  
  // Messages
  sendAnswerChange: (questionId: string, answer: any) => void;
  sendTestComplete: (results: any) => void;
  sendRecordingComplete: (recordingData: any) => void;
  sendError: (error: Error) => void;
}

const WebViewContext = createContext<WebViewContextValue | null>(null);

export function useWebView() {
  const context = useContext(WebViewContext);
  if (!context) {
    throw new Error('useWebView must be used within WebViewProvider');
  }
  return context;
}

interface WebViewProviderProps {
  children: ReactNode;
}

export function WebViewProvider({ children }: WebViewProviderProps) {
  const { isWebView, externalProps, isLoading, error: contextError } = useWebViewContext();
  const { isAuthenticated, isAuthenticating, authError, authenticate, logout } = useWebViewAuth();
  const { sendAnswerChange, sendTestComplete, sendRecordingComplete, sendError } = useWebViewMessages();

  const value: WebViewContextValue = {
    // Context
    isWebView,
    externalProps,
    isLoading,
    contextError,
    
    // Auth
    isAuthenticated,
    isAuthenticating,
    authError,
    authenticate,
    logout,
    
    // Messages
    sendAnswerChange,
    sendTestComplete,
    sendRecordingComplete,
    sendError,
  };

  return (
    <WebViewContext.Provider value={value}>
      {children}
    </WebViewContext.Provider>
  );
}